import { useContext, useMemo, useState } from "react";
import { HouseContext } from "../../../../context/HouseContext";
import { Link } from "react-router-dom";
import { FaPencilAlt, FaTrashAlt, FaStar } from "react-icons/fa";
import { FaSistrix, FaRegListAlt } from "react-icons/fa";
import { FaClipboardList } from "react-icons/fa6";
import { FaBed, FaBath } from "react-icons/fa";
import { deleteProperty } from "../../../../application/deleteProperty";
import Table from "../../../../components/Table/Table";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import {
  getStatusClasses,
  getTypeBadgeClasses,
} from "../../../../utils/propertyStatusColors";

function PropertyFeatured() {
  const { allHouses, removeHouse } = useContext(HouseContext);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [propertyIdToDelete, setPropertyIdToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [searchTerm, setSearchTerm] = useState("");

  const houses = allHouses || [];

  const filteredHouses = houses
    .filter((house) => house.highlights)
    .filter((house) =>
      house.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleConfirm = async () => {
    try {
      setIsDeleting(true);
      await deleteProperty(propertyIdToDelete);
      removeHouse(propertyIdToDelete);
      setIsConfirmOpen(false);
      setPropertyIdToDelete(null);
    } catch (error) {
      console.error("Error al eliminar la propiedad:", error);
      alert("Error al eliminar la propiedad. Por favor, inténtalo de nuevo.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setIsConfirmOpen(false);
    setPropertyIdToDelete(null);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Imagen",
        accessor: "images[0]",
        Cell: ({ value }) => (
          <img src={value} alt="" className="w-20 h-16 rounded-lg object-cover" />
        ),
      },
      {
        Header: "Nombre",
        accessor: "name",
        Cell: ({ value }) => <span className="capitalize font-medium">{value}</span>,
      },
      {
        Header: "Localidad",
        accessor: "country",
        Cell: ({ value }) => <span className="capitalize">{value}</span>,
      },
      {
        Header: "Tipo",
        accessor: "type",
        Cell: ({ value }) => (
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${getTypeBadgeClasses(
              value
            )}`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Estado",
        accessor: "state",
        Cell: ({ value }) => (
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusClasses(
              value
            )}`}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Editar",
        accessor: "id",
        Cell: ({ value }) => (
          <Link to={`/dashboard/propiedades/editar/${value}`}>
            <FaPencilAlt className="text-lg text-gray-700 dark:text-gray-300 w-full" />
          </Link>
        ),
      },
      {
        Header: "Eliminar",
        accessor: "idFirebase",
        Cell: ({ value }) => (
          <button
            onClick={() => {
              setPropertyIdToDelete(value);
              setIsConfirmOpen(true);
            }}
            className="text-red-500 hover:text-red-700"
            disabled={isDeleting}
          >
            <FaTrashAlt className="text-lg w-full" />
          </button>
        ),
      },
    ],
    [isDeleting]
  );

  return (
    <div className="px-4 md:px-6 py-4">
      <ConfirmDialog
        isOpen={isConfirmOpen}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        message="¿Estás seguro de que quieres eliminar esta propiedad?"
        isDeleting={isDeleting}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <FaStar className="text-yellow-400 w-5 h-5" />
          <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">
            Propiedades Destacadas
          </h1>
          <span className="text-sm bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full font-medium">
            {filteredHouses.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded-lg ${
              viewMode === "list"
                ? "bg-primary text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
          >
            <FaRegListAlt className="text-lg" />
          </button>
          <button
            onClick={() => setViewMode("cards")}
            className={`p-2 rounded-lg ${
              viewMode === "cards"
                ? "bg-primary text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
          >
            <FaClipboardList className="text-lg" />
          </button>
        </div>
      </div>

      {viewMode === "list" ? (
        <Table
          columns={columns}
          data={filteredHouses}
          title={"Propiedades Destacadas"}
        />
      ) : (
        <div>
          {/* Search */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {filteredHouses.length} registros
            </p>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar propiedad..."
                className="pl-4 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm w-full sm:w-64 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              />
              <FaSistrix className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredHouses.map((house) => (
              <div
                key={house.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700"
              >
                {/* Image with overlays */}
                <div className="relative">
                  <img
                    src={house.images?.[0]}
                    alt={house.name}
                    className="w-full h-48 object-cover"
                  />
                  <span
                    className={`absolute top-2 left-2 text-xs font-semibold px-2 py-1 rounded-full ${getStatusClasses(
                      house.state
                    )}`}
                  >
                    {house.state}
                  </span>
                  <span className="absolute top-2 right-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                    <FaStar className="w-3 h-3" /> Destacada
                  </span>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 capitalize truncate">
                      {house.name}
                    </h3>
                    {house.price && (
                      <span className="text-sm font-bold text-primary whitespace-nowrap ml-2">
                        ${Number(house.price).toLocaleString("es-AR")}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 truncate">
                    {house.country} - {house.address}
                  </p>
                  <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mb-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${getTypeBadgeClasses(
                        house.type
                      )}`}
                    >
                      {house.type}
                    </span>
                    {house.bedrooms && (
                      <span className="flex items-center gap-1">
                        <FaBed className="w-4 h-4" /> {house.bedrooms}
                      </span>
                    )}
                    {house.bathrooms && (
                      <span className="flex items-center gap-1">
                        <FaBath className="w-4 h-4" /> {house.bathrooms}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <Link
                      to={`/dashboard/propiedades/editar/${house.id}`}
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      <FaPencilAlt className="w-3 h-3" /> Editar
                    </Link>
                    <button
                      onClick={() => {
                        setPropertyIdToDelete(house.idFirebase);
                        setIsConfirmOpen(true);
                      }}
                      className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
                      disabled={isDeleting}
                    >
                      <FaTrashAlt className="w-3 h-3" /> Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertyFeatured;
